import React, { useEffect, useState } from 'react';
import { saveUser, updateUser } from '../../../services/users.js';
import Loading from '../../../components/loading/loading.js';
import Input from '../../../components/Input';
import {
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Button,
  Stack,
  Alert,
} from '@mui/material';
import { useAlert } from '../../../context/index.js';
import ChangePasswordForm from './ChangePassword.js';

const UserForm = props => {
  const concessionTypeOptions = [
    'Municipal Autônoma',
    'Autarquia',
    'Empresa Pública',
    'Concessionária Privada',
    'PPP',
    'Outros',
  ];

  const initialState = {
    name: '',
    email: '',
    cpf: '',
    role: 'common',
    image_url: '',
    phone: '',
    city: '',
    uf: '',
    mobile_operator_name: '',
    concession_type: '',
    company: '',
    position: '',
    is_admin: false,
    blocked: false,
    premium: false,
    errorPassword: false,
  };

  const [states, setStates] = useState(initialState);
  const [passwords, setPasswords] = useState({ password_confirm: '', password: '' });
  const [concessionFocused, setConcessionFocused] = useState(false);
  const { setShowError, setShowSuccess, setTextSuccessAlert, setTextErrorAlert } =
    useAlert();

  const handleSaveUser = () => {
    if (props.user === {} || props.user === undefined) {
      saveUser(states).then(
        response => {
          setStates({ ...initialState, role: 0 });
          setShowSuccess(true);
          setTextSuccessAlert('Usuário criado com sucesso!');
          props.setShowUserModal(prevState => ({ ...prevState, showUserModal: false }));
          setTimeout(() => window.location.reload(), 1400);
        },
        error => {
          setShowError(true);
          setTextErrorAlert('Email já registrado para outro usuário');
          props.setShowUserModal(prevState => ({ ...prevState, showUserModal: false }));
        }
      );
      return;
    } else {
      if (passwords.password === '' && passwords.password_confirm === '') {
        updateUser(states).then(
          response => {
            setShowSuccess(true);
            setTextSuccessAlert('Usuário editado com sucesso!');
            props.setShowUserModal(prevState => ({ ...prevState, showUserModal: false }));
            setTimeout(() => window.location.reload(), 1400);
          },
          error => {
            setShowError(true);
            setTextErrorAlert('Email já registrado para outro usuário');
            props.setShowUserModal(prevState => ({ ...prevState, showUserModal: false }));
          }
        );
        return;
      } else if (
        passwords.password_confirm === '' ||
        passwords.password !== passwords.password_confirm
      ) {
        setStates(prevState => ({ ...prevState, errorPassword: true }));
        return;
      } else {
        setStates(prevState => ({ ...prevState, errorPassword: false }));
        const updatePassword = updateUser(passwords);
        const updateUserWithoutPassword = updateUser(states);
        Promise.all([updatePassword, updateUserWithoutPassword])
          .then(response => {
            setShowSuccess(true);
            setTextSuccessAlert('Usuário editado com sucesso!');
            props.setShowUserModal(prevState => ({ ...prevState, showUserModal: false }));
            setTimeout(() => window.location.reload(), 1400);
          })
          .catch(error => {
            setShowError(true);
            setTextErrorAlert('Email já registrado para outro usuário');
            props.setShowUserModal(prevState => ({ ...prevState, showUserModal: false }));
          });
      }
    }
  };

  useEffect(() => {
    if (props.user !== {} && props.user !== undefined) {
      setStates(prevState => ({
        ...prevState,
        id: props.user.id,
        name: props.user.name,
        email: props.user.email,
        cpf: props.user.cpf,
        role: props.user.role === 1 ? 'admin' : 'common',
        image_url: props.user.image_url,
        phone: props.user.phone,
        city: props.user.city || '',
        uf: props.user.uf || '',
        mobile_operator_name: props.user.mobile_operator_name || '',
        concession_type: props.user.concession_type || '',
        company: props.user.company,
        position: props.user.position,
        is_admin: props.user.is_admin,
        blocked: props.user.blocked,
        premium: props.user.premium,
        error: false,
        success: false,
      }));
    }
  }, [props.user]);

  useEffect(() => {
    setStates(prevState => ({
      ...prevState,
      role: states.is_admin ? 'admin' : 'common',
      premium: states.is_admin ? true : prevState.premium,
    }));
  }, [states.is_admin, states.premium]);

  const SectionTitle = ({ children }) => (
    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.08em', mb: '16px', mt: '24px', borderBottom: '1px solid #f0f0f0', pb: '8px' }}>
      {children}
    </Typography>
  );

  const Row = ({ children }) => (
    <Box sx={{ display: 'flex', gap: '16px', width: '100%', mb: '16px', '@media (max-width:600px)': { flexDirection: 'column' } }}>
      {children}
    </Box>
  );

  return (
    <Box>
      {states.loading ? (
        <Loading />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>

          {/* Dados Pessoais */}
          <SectionTitle>Dados Pessoais</SectionTitle>
          <Row>
            <Box sx={{ flex: 1 }}>
              <Input required type="text" label="Nome" name="Name" value={states.name}
                onChange={e => setStates(prev => ({ ...prev, name: e.target.value }))} />
            </Box>
          </Row>
          <Row>
            <Box sx={{ flex: 1 }}>
              <Input required type="email" label="Email" name="Email" value={states.email}
                onChange={e => setStates(prev => ({ ...prev, email: e.target.value }))} />
            </Box>
          </Row>
          <Row>
            <Box sx={{ flex: 1 }}>
              <Input type="phone" label="Telefone" name="Telefone" value={states.phone}
                onChange={e => setStates(prev => ({ ...prev, phone: e.target.value }))} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Input type="text" label="CPF" name="CPF" value={states.cpf}
                onChange={e => setStates(prev => ({ ...prev, cpf: e.target.value }))} />
            </Box>
          </Row>

          {/* Localização */}
          <SectionTitle>Localização</SectionTitle>
          <Row>
            <Box sx={{ flex: 2 }}>
              <Input type="text" label="Cidade" name="city" value={states.city}
                onChange={e => setStates(prev => ({ ...prev, city: e.target.value }))} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Input type="text" label="UF" name="uf" value={states.uf} maxLength={2}
                onChange={e => setStates(prev => ({ ...prev, uf: e.target.value.toUpperCase() }))} />
            </Box>
          </Row>
          <Row>
            <Box sx={{ flex: 1 }}>
              <Input type="text" label="Operadora do celular" name="mobile_operator_name" value={states.mobile_operator_name}
                onChange={e => setStates(prev => ({ ...prev, mobile_operator_name: e.target.value }))} />
            </Box>
          </Row>

          {/* Empresa */}
          <SectionTitle>Empresa</SectionTitle>
          <Row>
            <Box sx={{ flex: 1 }}>
              <Input type="text" label="Empresa" name="company" value={states.company}
                onChange={e => setStates(prev => ({ ...prev, company: e.target.value }))} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Input type="text" label="Cargo" name="post" value={states.position}
                onChange={e => setStates(prev => ({ ...prev, position: e.target.value }))} />
            </Box>
          </Row>
          <Row>
            <Box sx={{ flex: 1 }}>
              <div className="material-textfield">
                <select
                  value={states.concession_type}
                  onChange={e => setStates(prev => ({ ...prev, concession_type: e.target.value }))}
                  onFocus={() => setConcessionFocused(true)}
                  onBlur={() => setConcessionFocused(false)}
                  style={{ width: '100%', fontSize: '1rem', fontWeight: 'normal', outline: 'none', border: '1px solid #6d6e71', borderRadius: '5px', padding: '0.71rem 0.7rem', color: '#001f27', backgroundColor: 'white', cursor: 'pointer', marginBottom: 0 }}
                >
                  <option value=""></option>
                  {concessionTypeOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                <label style={{ top: concessionFocused || states.concession_type ? '0' : '35%', transform: concessionFocused || states.concession_type ? 'translateY(-50%) scale(0.9)' : 'translateY(-50%)' }}>
                  Tipo de concessão
                </label>
              </div>
            </Box>
          </Row>

          {/* Permissões */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant="h4"
              sx={{
                margin: '24px 0 15px',
                fontWeight: 400,
                fontSize: '1.6rem !important',
                lineHeight: '20px',
                '@media (max-width:600px)': {
                  fontSize: '1.4rem !important',
                },
              }}
            >
              Permissões
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={states.is_admin}
                  onChange={e =>
                    setStates(prevState => ({
                      ...prevState,
                      is_admin: e.target.checked,
                    }))
                  }
                />
              }
              label="Administrador"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={states.premium}
                  onChange={e => {
                    if (states.is_admin) {
                      setStates(prevState => ({
                        ...prevState,
                        premium: true,
                      }));
                      return;
                    }
                    setStates(prevState => ({
                      ...prevState,
                      premium: e.target.checked,
                    }));
                  }}
                />
              }
              label="Premium"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={states.blocked}
                  onChange={e => {
                    setStates(prevState => ({
                      ...prevState,
                      blocked: e.target.checked,
                    }));
                  }}
                />
              }
              label="Bloqueado"
            />
          </Box>
          {(props.user !== undefined) && (
            <ChangePasswordForm
              passwords={passwords}
              user={props.user}
              setPasswords={setPasswords}
            />
          )}
          {states.errorPassword && (
            <Stack sx={{ width: '100%', marginBottom: '15px' }}>
              <Alert severity="error">As senhas informadas são diferentes.</Alert>
            </Stack>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: '20px' }}>
            {props.user?.id && props.onDelete ? (
              <Button
                type="button"
                variant="outlined"
                onClick={props.onDelete}
                sx={{
                  fontWeight: 600, height: '44px', borderRadius: '10px',
                  color: '#c62828', borderColor: '#c62828',
                  '&:hover': { backgroundColor: '#fdecea', borderColor: '#c62828' },
                }}
              >
                Excluir
              </Button>
            ) : <Box />}
            <Button
              type="button"
              variant="contained"
              onClick={handleSaveUser}
              className="btns"
              sx={{
                fontWeight: 600, width: '150px', height: '44px',
                borderRadius: '10px', backgroundColor: 'primary.main',
              }}
            >
              Salvar
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UserForm;
